import * as Comp from '../../../src/features/brewer/components'
import * as Layout from '../../../src/components/layout'
import { Locations } from 'src/components/Locations'

import { Brand, CompositeProduct, InputGetBySlug, Location } from 'src/types/schema/graphql'
import { GetServerSideProps, NextPage } from 'next'
import React, { useEffect } from 'react'

import { IDynamicData } from 'src/features/seo/types'
import SEOTags from 'src/features/seo/components/SEOTags'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import StructuredData from 'src/features/seo/components/StructuredData'
import { TabConfigItem } from 'src/components/layout/LinkTabsRow'
import createLinkTabsStore from 'src/hooks/createLinkTabsStore'
import createStickyNavStore from 'src/hooks/createStickyNavStore'
import gsap from 'gsap'
import { parseBrandMeta } from 'src/features/seo/utils/helpers'
import { useBrewsListStore } from 'src/features/brewsList/state/useBrewsListStore'
import { useRouter } from 'next/router'
import { getBrandBySlug } from 'src/features/brewer/utils/graph'
import { apiRequest, RequestInput } from 'src/api/apiRequest'
import { IsThisYourBreweryModal } from 'src/features/locations/components/IsThisYourBreweryModal'

const brewerPageStickyStore = createStickyNavStore('brewerPageStickyNav')
const brewerPageLinkTabs = createLinkTabsStore('brewerPageLinkTabs')

gsap.registerPlugin(ScrollTrigger)

const Page: NextPage<{
    brewer: Brand
    locations: Location[]
    products: CompositeProduct[]
    seoData: IDynamicData
}> = ({ brewer, locations, products, seoData }) => {
    const { paginatedProducts, page, styleFilter, searchTerm, availabilityFilter } =
        useBrewsListStore()
    const { isSticky, setIsSticky } = brewerPageStickyStore()
    const { selectedElement, setSelectedElement, setLinkTabsConfig } = brewerPageLinkTabs()

    const router = useRouter()
    const headerRef = React.useRef<HTMLDivElement>()
    const aboutRef = React.useRef<HTMLDivElement>()
    const locationsRef = React.useRef<HTMLDivElement>()
    const brewsRef = React.useRef<HTMLDivElement>()
    const stickyHeaderRef = React.useRef<HTMLDivElement>()

    const tabsConfig: TabConfigItem[] = [
        {
            id: 0,
            text: 'About',
            tag: 'about',
            action: () => router.push('#about', undefined, { shallow: true }),
        },
        {
            id: 1,
            text: 'Locations',
            tag: 'locations',
            action: () => router.push('#locations', undefined, { shallow: true }),
        },
        {
            id: 2,
            text: 'Brews',
            tag: 'brews',
            action: () => router.push('#brews', undefined, { shallow: true }),
        },
    ]

    useEffect(() => {
        setLinkTabsConfig(tabsConfig)
    }, [])

    useEffect(() => {
        return () => {
            router.push('')
        }
    }, [])

    React.useEffect(() => {
        ScrollTrigger.killAll()
        ScrollTrigger.create({
            trigger: stickyHeaderRef?.current,
            start: 'bottom top+=25',
            end: 'bottom bottom',
            // markers: true, // keep for easy debugging
            onLeaveBack: () => {
                setIsSticky(false)
                setSelectedElement('about')
            },
            onLeave: () => setIsSticky(true),
        })

        ScrollTrigger.create({
            trigger: aboutRef.current,
            start: `top top+=${headerRef?.current?.offsetHeight}`,
            end: `bottom top+=${headerRef?.current?.offsetHeight}`,
            onEnterBack: () => setSelectedElement('about'),
            onEnter: ({ isActive }) => isActive && setSelectedElement('about'),
            onLeave: () => setSelectedElement('locations'),
        })

        ScrollTrigger.create({
            trigger: locationsRef.current,
            start: `top top+=${headerRef?.current?.offsetHeight}`,
            end: `bottom top+=${headerRef?.current?.offsetHeight}`,
            onEnterBack: ({ isActive }) => isActive && setSelectedElement('locations'),
            onEnter: ({ isActive }) => isActive && setSelectedElement('locations'),
            onLeave: ({ isActive }) => !isActive && setSelectedElement('brews'),
        })

        ScrollTrigger.create({
            trigger: brewsRef.current,
            start: `top top+=${headerRef?.current?.offsetHeight}`,
            end: `bottom top+=${headerRef?.current?.offsetHeight}`,
            onEnterBack: ({ isActive }) => isActive && setSelectedElement('brews'),
            onEnter: ({ isActive }) => isActive && setSelectedElement('brews'),
        })

        return () => {
            ScrollTrigger.killAll()
        }
    }, [paginatedProducts, page, styleFilter, searchTerm, availabilityFilter])

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                left: 0,
            })
        }, 0)
    }, [router.pathname])

    return (
        <div className='bdb-page--brand'>
            <>
                <SEOTags schemaType='brand' dynamicData={seoData} />
                <StructuredData schemaType='brand' dynamicData={seoData} />
                <Layout.PageLayout isSticky={isSticky}>
                    <div ref={stickyHeaderRef}>
                        <Comp.BreadCrumbs brewer={brewer} />
                    </div>
                    <div className=' mx-auto flex flex-col justify-center'>
                        <div className='sticky top-0 z-20 w-full' ref={headerRef}>
                            <div className='mx-auto flex w-full flex-row md:px-4 lg:px-0'>
                                <Layout.Header brand={brewer} isSticky={isSticky} />
                            </div>
                            <div className='w-full'>
                                <Layout.LinkTabsRow
                                    tabs={tabsConfig}
                                    tabStore={brewerPageLinkTabs()}
                                    stickyStore={brewerPageStickyStore()}
                                    brandId={brewer?.id}
                                />
                            </div>
                        </div>

                        <div
                            className={
                                'mx-auto  flex w-full flex-col-reverse  px-4 md:flex-row md:space-x-4  xl:max-w-[1440px]  xl:space-x-8 xl:px-20'
                            }
                        >
                            <div className='mt-12 md:w-3/5 lg:w-2/3'>
                                <div className=' space-y-12'>
                                    <Layout.About
                                        description={brewer?.description}
                                        pageLocation='brewer'
                                        selectedElement={selectedElement}
                                        aboutRef={aboutRef}
                                    />
                                    {locations.length > 0 && (
                                        <Locations
                                            locations={locations}
                                            locationsRef={locationsRef}
                                        />
                                    )}

                                    {products?.length > 0 && (
                                        <Comp.Brews allProducts={products} brewsRef={brewsRef} />
                                    )}
                                </div>
                            </div>
                            <div className=' mt-12 space-y-10 md:w-1/2 lg:w-2/5'>
                                <Comp.Details brewer={brewer} />
                            </div>
                        </div>
                    </div>

                    <section className='container mx-auto max-w-screen-xl px-2'></section>
                </Layout.PageLayout>
            </>
            <IsThisYourBreweryModal brand={brewer} />
        </div>
    )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const { params } = context
    let brewer = {} as Brand
    let locations = [] as Location[]
    let products = [] as CompositeProduct[]
    let seoData = null
    const itemSlug = String(params?.slug)
    const input: RequestInput<InputGetBySlug> = {
        query: getBrandBySlug,
        args: { input: { itemSlug } },
        file: 'pages/brewer/[slug].ts',
        endpoint: 'fetchBrand',
        noAlerts: true,
        publicQuery: true,
    }
    const { data, success } = await apiRequest<Brand, InputGetBySlug>(input)
    if (success) {
        brewer = data
        locations = data?.locations
        products = data?.products
        seoData = parseBrandMeta(data)
    }
    if (!data) {
        return {
            notFound: true,
        }
    }

    return {
        props: { brewer, locations, products, seoData },
    }
}

export default Page
